import { BOOLEAN_SELECT_LIST_ITEMS, Stack, ValidationErrors, pn } from "@compass/core";
import { DatePicker, Select } from "components";
import { MatchReportContributorDetailFilter, MatchReportContributorDetailResource } from "generated/openApi";
import produce from "immer";
import { useEffect, useState } from "react";
import ApiService from "services/apiService";
import { MatchReportGlobalFilter } from "../MatchReportPage";

export const ContributorDetailFilter = (props: {
  filter: MatchReportContributorDetailFilter;
  setFilter: (value: MatchReportContributorDetailFilter) => void;
  globalFilter: MatchReportGlobalFilter;
  setGlobalFilter: (value: MatchReportGlobalFilter) => void;
  validationErrors: ValidationErrors;
}) => {
  const [resource, setResource] = useState<MatchReportContributorDetailResource>({});
  const properties = resource.filterProperties || [];
  const subPartners = resource.subPartners?.filter((x) => props.filter.partnerIds?.includes(x.partnerId)) || [];

  useEffect(() => {
    fetchResource();
  }, []);

  useEffect(() => {
    props.setFilter(
      produce(props.filter, (draft) => {
        if (props.globalFilter.dateFrom) draft.dateFrom = props.globalFilter.dateFrom;
        if (props.globalFilter.dateTo) draft.dateTo = props.globalFilter.dateTo;
        draft.partnerIds = props.globalFilter.partnerIds;
        draft.subPartnerIds = props.globalFilter.subPartnerIds;
        draft.contributorId = props.globalFilter.contributorId;
        draft.contributor = props.globalFilter.contributor;
        draft.isMatch = props.globalFilter.isMatch;
        draft.reviewId = props.globalFilter.reviewId;
      })
    );
  }, []);

  const fetchResource = () => {
    ApiService.matchReports.getContributorDetailResource().then((response) => {
      setResource(response);
    });
  };

  const dateFromField = () => {
    const propertyName = pn<MatchReportContributorDetailFilter>("dateFrom");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <DatePicker
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.filter.dateFrom}
        onChange={(value) => {
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.dateFrom = value;
            })
          );
          props.setGlobalFilter(
            produce(props.globalFilter, (draft) => {
              draft.dateFrom = value;
            })
          );
        }}
      />
    );
  };

  const dateToField = () => {
    const propertyName = pn<MatchReportContributorDetailFilter>("dateTo");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <DatePicker
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.filter.dateTo}
        onChange={(value) => {
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.dateTo = value;
            })
          );
          props.setGlobalFilter(
            produce(props.globalFilter, (draft) => {
              draft.dateTo = value;
            })
          );
        }}
      />
    );
  };

  const partnersField = () => {
    const propertyName = pn<MatchReportContributorDetailFilter>("partnerIds");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        multiple
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={resource.partners?.filter((x) => props.filter.partnerIds?.includes(x.id))}
        options={resource.partners}
        getOptionLabel={(option) => option.name}
        onChange={(e, values) => {
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.partnerIds = values.map((x) => x.id);
              draft.subPartnerIds = undefined;
            })
          );
          props.setGlobalFilter(
            produce(props.globalFilter, (draft) => {
              draft.partnerIds = values.map((x) => x.id);
              draft.subPartnerIds = undefined;
            })
          );
        }}
        isOptionEqualToValue={(option, value) => option.id === value.id}
      />
    );
  };

  const subPartnersField = () => {
    const propertyName = pn<MatchReportContributorDetailFilter>("subPartnerIds");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        multiple
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={resource.subPartners?.filter((x) => props.filter.subPartnerIds?.includes(x.id))}
        options={resource.subPartners?.filter(x => props.filter.partnerIds?.includes(x.partnerId))}
        getOptionLabel={(option) => option.name}
        onChange={(e, values) => {
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.subPartnerIds = values.map((x) => x.id);
            })
          );
          props.setGlobalFilter(
            produce(props.globalFilter, (draft) => {
              draft.subPartnerIds = values.map((x) => x.id);
            })
          );
        }}
        isOptionEqualToValue={(option, value) => option.id === value.id}
      />
    );
  };

  const contributorField = () => {
    const propertyName = pn<MatchReportContributorDetailFilter>("contributorId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.filter.contributor}
        getOptionLabel={(option) => `${option.lastName}, ${option.firstName}`}
        onChange={(e, value) => {
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.contributorId = value?.id;
              draft.contributor = value;
            })
          );
          props.setGlobalFilter(
            produce(props.globalFilter, (draft) => {
              draft.contributorId = value?.id;
              draft.contributor = value;
            })
          );
        }}
        fetchFunction={(searchString: string) => ApiService.search.contributors(searchString)}
        isOptionEqualToValue={(option, value) => option.id === value.id}
      />
    );
  };

  const reviewField = () => {
    const propertyName = pn<MatchReportContributorDetailFilter>("reviewId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={resource.matchReviews?.find((x) => x.id === props.filter.reviewId)}
        options={resource.matchReviews}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) => {
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.reviewId = value?.id;
            })
          );
          props.setGlobalFilter(
            produce(props.globalFilter, (draft) => {
              draft.reviewId = value?.id;
            })
          );
        }}
      />
    );
  };

  const isMatchField = () => {
    const propertyName = pn<MatchReportContributorDetailFilter>("isMatch");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id === props.filter.isMatch)}
        options={BOOLEAN_SELECT_LIST_ITEMS}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) => {
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.isMatch = value?.id;
            })
          );
          props.setGlobalFilter(
            produce(props.globalFilter, (draft) => {
              draft.isMatch = value?.id;
            })
          );
        }}
      />
    );
  };

  return (
    <Stack spacing="sm">
      {dateFromField()}
      {dateToField()}
      {partnersField()}
      {subPartners.length > 0 && <>{subPartnersField()}</>}
      {contributorField()}
      {reviewField()}
      {isMatchField()}
    </Stack>
  );
};
